import { useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { EnterButton } from '@backrs_ui/grow-ui/src/components/Lubav/EnterButton';

import { Box, SxProps, TextField, Theme, Typography } from '@mui/material';

import { SkipChatButton } from './SkipChatButton';

type MultiNumericChatQuestionProps = Readonly<{
  idPrefix?: string;
  options: readonly string[];
  required: boolean;
  isTyping: boolean;
  onSubmit: (formatted: string) => void;
  onSkip: () => void;
}>;

export const MultiNumericChatQuestion = ({
  idPrefix = 'multi-numeric-chat-question',
  options,
  required,
  isTyping,
  onSubmit,
  onSkip,
}: MultiNumericChatQuestionProps) => {
  const [values, setValues] = useState<Record<string, string>>({});

  const handleChange = (label: string, raw: string) => {
    // Only allow digits and a single decimal point.
    if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;

    setValues((prev) => ({ ...prev, [label]: raw }));
  };

  const filledCount = options.filter((label) => (values[label] ?? '').trim() !== '').length;
  // Submit should always require at least one filled field — an empty submit conveys
  // no answer. If the question is optional, Skip is the way to move past it.
  const isDisabled = isTyping || filledCount === 0;

  const handleSubmit = () => {
    const parts = options
      .map((label) => {
        const v = (values[label] ?? '').trim();

        return v ? `${label}: ${v}` : null;
      })
      .filter((v): v is string => v !== null);

    onSubmit(parts.join(', '));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isDisabled) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box sx={containerSx}>
      <Box sx={gridSx}>
        {options.map((label) => (
          <Box key={label} sx={fieldSx}>
            <Typography variant="text-sm-semibold">{label}</Typography>
            <TextField
              id={`${idPrefix}-${label.toLowerCase().replace(/\s+/g, '-')}`}
              size="small"
              value={values[label] ?? ''}
              onChange={(e) => handleChange(label, e.target.value)}
              onKeyDown={handleKeyDown}
              inputMode="decimal"
              placeholder="—"
              disabled={isTyping}
            />
          </Box>
        ))}
      </Box>
      <Box sx={actionsRowSx}>
        <EnterButton
          id={`${idPrefix}-submit`}
          onClick={handleSubmit}
          label={appStrings.modules.solutionSessions.questions.submit}
          showEnterHint
          disabled={isDisabled}
        />
        {required ? (
          <Box />
        ) : (
          <SkipChatButton id={`${idPrefix}-skip`} disabled={isTyping} onSkip={onSkip} />
        )}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  gap: backrs.spacing['spacing-sm'],
});

const fieldSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
});

const actionsRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
};
